/**
 * @classdesc Auxilia a interação com funcionalidades do S.O.
 */
class SystemUtil {
    /**
     * Cria um arquivo CSV para salvar na máquina do cliente.
     *
     * @param {string} nomeArquivo O nome do arquivo a ser exportado.
     * @param {string} conteudoArquivo O conteúdo a ser escrito no arquivo.
     * @returns {void}
     */
    static exportarAquivoCsv(nomeArquivo, conteudoArquivo) {
        const blob = new Blob(['\uFEFF' + conteudoArquivo], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', nomeArquivo);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Converte um array binário para uma string Base64.
     *
     * @param {Array<number>} blob O array binário.
     * @returns {Promise<string>}
     */
    static blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Copia o texto para área de transferência.
     *
     * @see {@link "https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard#using_the_clipboard_api"}
     * @param {string} texto O texto a ser copiado
     * @returns {Promise<boolean>} Um sinalizador que indica se o texto foi enviado para a área de transferência (true) ou não (false)
     */
    static async copiarParaAreaDeTransferencia(texto) {
        // Tenta copiar usando a API moderna, se disponível e em contexto seguro
        let copiado = await SystemUtil.#updateClipboard(texto);

        // Caso falhe (ex: navegador incompatível ou erro inesperado), tenta método alternativo
        if (!copiado) {
            copiado = SystemUtil.#unsecuredCopyToClipboard(texto);
        }

        if (!copiado) {
            console.error('Falha ao tentar copiar o texto para a área de transferência.');
        }

        return copiado;
    }

    /**
     * Tenta copiar o texto utilizando a API moderna do navegador.
     * Funciona apenas em contextos seguros (HTTPS).
     *
     * @param {string} texto O texto a ser copiado
     * @returns {Promise<boolean>} Indica se o texto foi copiado com sucesso
     */
    static async #updateClipboard(texto) {
        if (window.isSecureContext && navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(texto);
                return true;
            } catch (error) {
                console.log('Erro ao tentar copiar o texto usando navigator.clipboard.writeText:', error);
                return false;
            }
        }

        return false;
    }

    /**
     * Tenta copiar o texto utilizando uma abordagem alternativa via DOM.
     * Funciona em ambientes menos seguros ou navegadores legados.
     *
     * @see {@link "https://stackoverflow.com/questions/71873824/copy-text-to-clipboard-cannot-read-properties-of-undefined-reading-writetext"}
     * @see {@link "https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript"}
     * @param {string} text O texto a ser copiado
     * @returns {boolean} Um sinalizador que indica se o texto foi enviado para a área de transferência (true) ou não (false)
     */
    static #unsecuredCopyToClipboard(text) {
        let copiado = false;
        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = text;

        // Evita rolagem ao focar
        tempTextArea.style.top = '0';
        tempTextArea.style.left = '0';
        tempTextArea.style.position = 'fixed';

        document.body.appendChild(tempTextArea);
        tempTextArea.setAttribute('readonly', 'false');
        tempTextArea.focus();
        tempTextArea.select();
        tempTextArea.setSelectionRange(0, text.length); // Compatibilidade com dispositivos móveis

        try {
            copiado = document.execCommand('copy');
        } catch (err) {
            console.log('Erro ao tentar copiar o texto usando execCommand:', err);
        }

        document.body.removeChild(tempTextArea);
        return copiado;
    }

    /*
        Função de debounce para limitar a quantidade de chamadas de funções
    */
    static debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func(...args), delay);
        };
    };
}

export default SystemUtil;
